import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Sparkles, Loader2, CheckCircle2, ChevronRight, Wallet, Cpu, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";

/** Format INR from paise-style number (M-3 estimation returns rupees). */
function fmtINR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

/** Cost Preview Widget — indicative price band from the M-3 estimation. */
function CostPreview({ estimation }) {
  if (!estimation) return null;
  const low = estimation.customer_price_low;
  const high = estimation.customer_price_high;
  const weeksLow = estimation.estimated_duration_weeks_low;
  const weeksHigh = estimation.estimated_duration_weeks_high;
  const conf = Math.round((estimation.confidence || 0) * 100);
  return (
    <div className="rounded-2xl border border-[#E05E36]/30 bg-gradient-to-br from-white to-orange-50 p-5" data-testid="cost-preview">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#E05E36]">Indicative price</div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500">Confidence · {conf}%</div>
      </div>
      <div className="text-3xl sm:text-4xl font-black text-stone-900 tabular-nums" data-testid="cost-preview-band">
        {fmtINR(low)} <span className="text-stone-400 font-bold text-2xl">→</span> {fmtINR(high)}
      </div>
      <div className="mt-2 text-sm text-stone-600">
        Estimated build time · <span className="font-bold text-stone-900">{weeksLow}–{weeksHigh} weeks</span>
      </div>
      <div className="mt-3 text-[11px] text-stone-500 leading-relaxed">
        Indicative only. Your final quote is confirmed after a builder accepts your project.
        Money is held in escrow until you approve the preview.
      </div>
    </div>
  );
}

/** Clarification questions surfaced from M-3 for the customer to answer. */
function ClarificationCard({ questions }) {
  if (!questions || questions.length === 0) return null;
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="clarification-card">
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">A few clarifying questions</div>
      <ul className="space-y-2">
        {questions.slice(0, 6).map((q, i) => (
          <li key={i} className="text-sm text-stone-700 flex items-start gap-2" data-testid={`clarification-q-${i}`}>
            <ChevronRight size={14} className="text-[#E05E36] shrink-0 mt-1" />
            <span>{q}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Trust strip — how the money and delivery actually work. */
function TrustStrip() {
  const items = [
    { icon: Cpu,         label: "AI-designed spec",  copy: "We turn your idea into a builder-ready plan" },
    { icon: Wallet,      label: "Escrowed payment",  copy: "Money held safe until you approve the preview" },
    { icon: ShieldCheck, label: "Independent QA",    copy: "We verify what the builder ships before you see it" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white p-3">
          <div className="w-8 h-8 rounded-full bg-[#E05E36]/10 grid place-items-center shrink-0">
            <it.icon size={16} className="text-[#E05E36]" />
          </div>
          <div>
            <div className="text-[13px] font-black text-stone-900">{it.label}</div>
            <div className="text-[11px] text-stone-500 leading-snug">{it.copy}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Marketplace() {
  const navigate = useNavigate();
  const location = useLocation();
  const { project_id: routeProjectId } = useParams() || {};

  // Prefilled text: from ?q= query or from location.state.text (AINA hookup).
  const initialText = (() => {
    const s = new URLSearchParams(location.search);
    return (location.state && location.state.text) || s.get("q") || "";
  })();

  const [text, setText] = useState(initialText);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [intake, setIntake] = useState(null);
  const [project, setProject] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState(null);

  // Auto-submit if we arrived with prefilled text from AINA.
  useEffect(() => {
    if (initialText && location.state && location.state.autoSubmit) {
      submitIntake(initialText).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If we arrived with a routeProjectId, load its state on mount.
  useEffect(() => {
    if (!routeProjectId) return;
    (async () => {
      try {
        const { data } = await api.get(`/marketplace/projects/${routeProjectId}`);
        setProject(data);
        // Pull the last intake snapshot off the project doc if present.
        if (data.mkt_intake) {
          setIntake({
            understanding:            data.mkt_intake.understanding,
            classification:           data.mkt_intake.classification,
            estimation:               data.mkt_intake.estimation,
            specification:            data.mkt_intake.specification,
            clarification_questions:  data.mkt_intake.clarification_questions || [],
            confidence:               data.mkt_intake.confidence,
            low_confidence:           data.mkt_intake.low_confidence,
          });
        }
      } catch (e) {
        // Silent — a fresh visit to /marketplace/:id from a bad link is fine.
      }
    })();
  }, [routeProjectId]);

  async function submitIntake(rawText) {
    setError(null); setIntake(null);
    setSubmitting(true);
    try {
      const body = { raw_requirement: (rawText || text).trim() };
      if (project?.project_id) body.project_id = project.project_id;
      const { data } = await api.post("/marketplace/intake", body);
      setProject(data.project);
      setIntake(data.intake);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  }

  async function startCheckout() {
    if (!project?.project_id) return;
    setPayError(null); setPayLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post(
        `/marketplace/projects/${project.project_id}/checkout`,
        { origin_url: origin },
      );
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setPayError("Couldn't open Stripe checkout. Please retry.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setPayError("Please sign in to continue with payment.");
      else setPayError(e?.response?.data?.detail || "Payment could not be started.");
    } finally {
      setPayLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#E05E36] font-bold">
          <Sparkles size={12} /> Software marketplace
        </div>
        <h1
          className="heading text-3xl sm:text-4xl md:text-5xl font-black text-stone-900 mt-2 leading-[1.05]"
          data-testid="marketplace-h1"
        >
          Describe what you want — <span className="text-[#E05E36]">we'll build it.</span>
        </h1>
        <p className="mt-3 text-stone-600 text-sm sm:text-base max-w-2xl">
          Tell us your idea in plain English. Our AI turns it into a builder-ready plan with an
          indicative price. Approve the plan, pay through Stripe (held in escrow), and a vetted
          builder starts the work. You only release payment after you preview and approve.
        </p>

        <div className="mt-6 rounded-3xl bg-white border border-stone-200 shadow-sm p-4 sm:p-5">
          <label htmlFor="mkt-req" className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
            Your project idea
          </label>
          <textarea
            id="mkt-req"
            data-testid="marketplace-req"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder="e.g. I want a website for my school with student attendance, fees online, and a parent login. Should also work on mobile."
            className="mt-2 w-full rounded-2xl border border-stone-200 focus:border-[#E05E36] focus:ring-2 focus:ring-[#E05E36]/20 outline-none p-3 text-sm text-stone-800 min-h-[140px]"
            disabled={submitting}
          />
          <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
            <div className="text-[11px] text-stone-500">
              We never share your idea outside our vetted builders. No sensitive data (API keys, passwords) — those are stripped automatically.
            </div>
            <button
              type="button"
              onClick={() => submitIntake()}
              disabled={submitting || text.trim().length < 3}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-white bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="marketplace-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {submitting ? "Analyzing…" : "Get my price"}
            </button>
          </div>
          {error && (
            <div className="mt-3 text-sm text-red-600" data-testid="marketplace-error">{error}</div>
          )}
        </div>

        {!intake && !submitting && <TrustStrip />}

        {intake && (
          <div className="mt-6 space-y-4" data-testid="intake-result">
            {/* Summary of what we understood */}
            {intake.understanding && (
              <div className="rounded-2xl border border-stone-200 bg-white p-5">
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">
                  What we understood
                </div>
                <div className="text-[15px] text-stone-900 font-bold">
                  {intake.understanding.summary || intake.understanding.primary_goal}
                </div>
                {intake.classification && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(intake.classification.categories || []).slice(0, 5).map((c) => (
                      <span key={c} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-stone-100 text-stone-700">
                        {c.replace(/_/g, " ")}
                      </span>
                    ))}
                    {intake.classification.platform && (
                      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#E05E36]/10 text-[#c94e2a]">
                        {intake.classification.platform}
                      </span>
                    )}
                    {intake.classification.project_size && (
                      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-stone-100 text-stone-700">
                        {intake.classification.project_size}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            <CostPreview estimation={intake.estimation} />
            <ClarificationCard questions={intake.clarification_questions} />

            {/* Payment CTA */}
            <div className="rounded-3xl border border-stone-200 bg-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Ready to start</div>
                <div className="text-[15px] font-black text-stone-900">Pay via Stripe (held in escrow)</div>
                <div className="text-[11px] text-stone-500 mt-1">
                  You approve the preview before the builder is paid.
                </div>
              </div>
              <button
                type="button"
                onClick={startCheckout}
                disabled={payLoading || !project?.project_id || !intake?.estimation}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold text-white bg-stone-900 hover:bg-stone-800 disabled:opacity-50"
                data-testid="marketplace-checkout"
              >
                {payLoading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {payLoading ? "Opening Stripe…" : "Approve & pay"}
              </button>
            </div>
            {payError && (
              <div className="text-sm text-red-600" data-testid="marketplace-pay-error">{payError}</div>
            )}
          </div>
        )}

        {submitting && (
          <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-8 flex flex-col items-center text-center" data-testid="marketplace-loading">
            <Loader2 size={28} className="text-[#E05E36] animate-spin" />
            <div className="mt-3 text-sm font-bold text-stone-900">Turning your idea into a plan…</div>
            <div className="text-[11px] text-stone-500 mt-1">
              Understanding · Classifying · Estimating · Writing spec
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
